import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/api";
import {
  Gauge, ChartBar, UsersThree, Cube, Factory, ClipboardText, SignOut, UserList, Stack, Gear, Database, CaretDown, CaretRight, Money, Truck, Package, Recycle, List, X, Key, Receipt, Storefront, UserCircle, WarningCircle, BookOpen, Wrench, CalendarBlank, ChatCircleDots, Flask, SealCheck,
} from "@phosphor-icons/react";
import CommandPalette from "@/components/CommandPalette";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// roles: who can see each link
const ALL_ROLE_LIST = ["manager", "super_admin", "prod_entry", "job_entry", "dispatch", "grn_entry", "paper_entry", "viewer", "sales", "spares_request", "accounts"];
const NON_MGR_ROLES = ["prod_entry", "job_entry", "dispatch", "grn_entry", "paper_entry", "viewer", "sales", "spares_request", "accounts"];
const TOP_NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, roles: ALL_ROLE_LIST },
  { to: "/sales-orders", label: "Sales Orders", icon: Receipt, roles: ["manager", "super_admin", "sales"] },
  { to: "/delivery-schedule", label: "Delivery Schedule", icon: CalendarBlank, roles: ["manager", "super_admin", "prod_entry", "job_entry", "dispatch", "grn_entry", "paper_entry", "viewer", "sales", "spares_request", "accounts"] },
  { to: "/jobs", label: "Jobs", icon: ClipboardText, roles: ["manager", "super_admin", "job_entry", "dispatch", "viewer"] },
];
const PROD_ROLES = ["manager", "super_admin", "prod_entry"];
const PRODUCTION_STAGES = [
  { to: "/production/board", label: "Board Plant" },
  { to: "/production/printing", label: "Printing" },
  { to: "/production/gluing", label: "Gluing" },
  { to: "/production/stitching", label: "Stitching" },
];
const TOP_NAV_AFTER = [
  { to: "/dispatch-slips", label: "Dispatch Slips", icon: Package, roles: ["manager", "super_admin", "dispatch"] },
  // Quality COA — placed right below Dispatch Slips for all roles that can use it.
  { to: "/reports/coa", label: "Quality COA", icon: SealCheck, roles: ["manager", "super_admin", "dispatch", "accounts"] },
  { to: "/wastage-consumables", label: "Wastage & Consumables", icon: Recycle, roles: ["manager", "super_admin", "prod_entry"] },
  // Station work-ready lists: what can be printed/glued/stitched/dispatched right now.
  { to: "/reports/stage-ready", label: "Stage Ready", icon: ClipboardText, roles: ALL_ROLE_LIST },
  // Daily production summary — managers reach it via the Reports group; give the rest a direct link.
  { to: "/reports/daily-summary", label: "Daily Summary", icon: ChartBar, roles: NON_MGR_ROLES },
  { to: "/spare-requests", label: "Spare Requests", icon: Wrench, roles: ["manager", "super_admin", "spares_request", "grn_entry", "accounts"] },
  { to: "/assistant", label: "AI Assistant", icon: ChatCircleDots, roles: ["super_admin"] },
  { to: "/rnd", label: "R&D Spec Tool", icon: Flask, roles: ["manager", "super_admin", "sales"] },
  // Sales Analytics — direct link for the sales role (managers reach it via the Reports group).
  { to: "/reports/sales-analytics", label: "Sales Analytics", icon: ChartBar, roles: ["sales"] },
  // Direct links for the dedicated stock roles (managers reach these via the Purchases group).
  { to: "/grn", label: "GRN", icon: Truck, roles: ["grn_entry"] },
  { to: "/consumption", label: "Consumption", icon: Recycle, roles: ["paper_entry"] },
  // Converter challans — direct link for the entry roles (managers reach it via the Purchases group).
  { to: "/converter-challans", label: "Converter Challans", icon: Factory, roles: ["paper_entry", "grn_entry", "prod_entry"] },
  { to: "/stock", label: "Stock", icon: Stack, roles: ["prod_entry", "job_entry", "dispatch", "grn_entry", "paper_entry", "viewer", "stock_view"] },
  // Stock Register (read-only ledger) — direct link for Stock View + Consumption roles (managers reach it via Reports).
  { to: "/reports/stock-register", label: "Stock Register", icon: ChartBar, roles: ["stock_view", "paper_entry"] },
];
// Purchases & Stock group (manager only) — sits below Wastage & Consumables.
const PAPER_PURCHASE = [
  { to: "/paper-orders", label: "Paper Orders" },
  { to: "/other-purchases", label: "Other Purchases" },
  { to: "/grn", label: "GRN Goods Receipt" },
  { to: "/consumption", label: "Consumption" },
  { to: "/stock", label: "Stock" },
  { to: "/stock/reconcile", label: "Stock Reconciliation" },
  { to: "/converter-challans", label: "Converter Challans" },
  { to: "/reports/po-pending", label: "PO Pending" },
  { to: "/reports/bf-monthly", label: "BF Price (Monthly)" },
  { to: "/reports/reorder-list", label: "Reorder List" },
];
// Masters now live in the top-right Settings menu (flat list — mobile friendly).
const MASTERS = [
  { to: "/parties", label: "Parties", icon: UsersThree },
  { to: "/items", label: "Items", icon: Cube },
  { to: "/other-items", label: "Other Items", icon: Cube },
  { to: "/machines", label: "Machines", icon: Gear },
  { to: "/spare-masters", label: "Machinery & Spares", icon: Wrench },
  { to: "/paper-prices", label: "Paper Prices", icon: Money },
  { to: "/suppliers", label: "Suppliers", icon: Storefront },
  { to: "/converters", label: "Converters", icon: Factory },
  { to: "/transport", label: "Transport", icon: Truck },
  { to: "/users", label: "Users", icon: UserList },
  { to: "/company", label: "Company Profile & Settings", icon: Database },
];
const REPORTS = [
  { to: "/reports/daily-summary", label: "Daily Summary" },
  { to: "/reports/sales-orders", label: "Sales Orders" },
  { to: "/reports/analytics", label: "Analytics" },
  { to: "/reports/wastage", label: "Production Wastage" },
  { to: "/reports/stage-weight", label: "Stage Weight" },
  { to: "/reports/margin", label: "Margin" },
  { to: "/reports/pl-per-kg", label: "P/L per Kg" },
  { to: "/reports/wastage-consumables", label: "Wastage & Consumables" },
  { to: "/reports/material-yield", label: "Material Yield" },
  { to: "/reports/conversion-billing", label: "Conversion Billing" },
  { to: "/reports/paper-planning", label: "Paper Planning" },
  { to: "/reports/paper-overspec", label: "Paper Over-spec Wastage" },
  { to: "/reports/outsourced-conversion", label: "Outsourced Conversion" },
  { to: "/reports/stock-register", label: "Stock Register" },
  { to: "/reports/purchase-register", label: "Purchase Register" },
  { to: "/reports/sales-register", label: "Sales Register" },
  { to: "/reports/sales-analytics", label: "Sales Analytics" },
  { to: "/reports/spares", label: "Spares & Maintenance" },
  { to: "/activity-log", label: "Activity Log" },
];

const roleLabels = {
  super_admin: "Super Admin", manager: "Manager", prod_entry: "Production Entry", job_entry: "Job Entry", dispatch: "Dispatch",
  grn_entry: "GRN Entry", paper_entry: "Consumption Entry", viewer: "Viewer",
  sales: "Sales", spares_request: "Spares Request", accounts: "Accounts",
};

function CollapsibleGroup({ icon: Icon, label, open, setOpen, testid, children }) {
  return (
    <div className="mt-2">
      <button type="button" data-testid={testid} onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-sm text-sm text-[var(--text-2)] hover:bg-zinc-100 hover:text-[var(--text-1)] transition-colors">
        <span className="flex items-center gap-3"><Icon size={18} weight="bold" /><span className="font-medium">{label}</span></span>
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </button>
      {open && <div className="ml-3 mt-1 border-l border-[var(--border)] pl-2">{children}</div>}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const role = user?.role;
  const myRoles = [role, ...(user?.extra_roles || [])].filter(Boolean);
  const isManager = myRoles.includes("manager") || myRoles.includes("super_admin");
  const reportsPaths = REPORTS.map((m) => m.to);
  const paperPaths = PAPER_PURCHASE.map((m) => m.to);
  const [reportsOpen, setReportsOpen] = useState(reportsPaths.includes(loc.pathname));
  const [paperOpen, setPaperOpen] = useState(paperPaths.includes(loc.pathname) || loc.pathname.startsWith("/paper-orders"));
  const [prodOpen, setProdOpen] = useState(loc.pathname.startsWith("/production"));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);

  useEffect(() => {
    if (!isManager) return;
    api.get("/reports/alerts")
      .then((r) => {
        const s = r.data.stock || {};
        setOverdueCount(
          (r.data.unreconciled || []).filter((x) => x.overdue).length +
          (s.aged_reels || []).length + (s.low_stock || []).length +
          (r.data.jobwork_mismatch || []).length
        );
      })
      .catch(() => setOverdueCount(0));
    api.get("/approvals/count")
      .then((r) => setPendingApprovals(r.data.pending || 0))
      .catch(() => setPendingApprovals(0));
  }, [isManager, loc.pathname]);

  useEffect(() => {
    if (reportsPaths.includes(loc.pathname)) setReportsOpen(true);
    if (loc.pathname.startsWith("/production")) setProdOpen(true);
    if (paperPaths.includes(loc.pathname) || loc.pathname.startsWith("/paper-orders")) setPaperOpen(true);
  }, [loc.pathname, reportsPaths, paperPaths]);
  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);

  const visibleTop = TOP_NAV.filter((n) => n.roles.some((r) => myRoles.includes(r)));
  const visibleAfter = TOP_NAV_AFTER.filter((n) => n.roles.some((r) => myRoles.includes(r)) &&
    // Managers get GRN/Consumption/Stock via the Purchases group — avoid duplicates.
    !(isManager && ["/grn", "/consumption", "/stock"].includes(n.to)));
  const showProduction = PROD_ROLES.some((r) => myRoles.includes(r));

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-150 ${
      isActive ? "bg-[var(--text-1)] text-white" : "text-[var(--text-2)] hover:bg-zinc-100 hover:text-[var(--text-1)]"
    }`;

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-40" data-testid="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      <aside
        className={`w-64 shrink-0 bg-[var(--sidebar)] border-r border-[var(--border)] flex flex-col fixed md:static inset-y-0 left-0 z-50 transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-[var(--border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[var(--text-1)] text-white flex items-center justify-center rounded-sm">
              <Stack size={20} weight="bold" />
            </div>
            <div>
              <div className="heading font-black text-lg leading-none">KAPSTONE OPS</div>
              <div className="overline mt-1">Plant Monitor &amp; Analyze</div>
            </div>
          </div>
          <button className="md:hidden text-[var(--text-3)]" data-testid="sidebar-close" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>

        <nav className="px-3 py-4 flex-1 overflow-y-auto no-scrollbar">
          {visibleTop.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`} className={linkClass}>
              <Icon size={18} weight="bold" /><span className="font-medium">{label}</span>
            </NavLink>
          ))}

          {showProduction && (
            <CollapsibleGroup icon={Factory} label="Production" open={prodOpen} setOpen={setProdOpen} testid="nav-production-toggle">
              <div data-testid="nav-production-group">
                {PRODUCTION_STAGES.map(({ to, label }) => (
                  <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`} className={linkClass}>
                    <Factory size={16} weight="bold" /><span className="font-medium">{label}</span>
                  </NavLink>
                ))}
              </div>
            </CollapsibleGroup>
          )}

          {visibleAfter.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`} className={linkClass}>
              <Icon size={18} weight="bold" /><span className="font-medium">{label}</span>
            </NavLink>
          ))}

          {isManager && (
            <CollapsibleGroup icon={Receipt} label="Purchases & Stock" open={paperOpen} setOpen={setPaperOpen} testid="nav-paper-toggle">
              <div data-testid="nav-paper-group">
                {PAPER_PURCHASE.map(({ to, label }) => (
                  <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/[\s()]+/g, "-").replace(/-+$/, "")}`} className={linkClass}>
                    <Receipt size={16} weight="bold" /><span className="font-medium">{label}</span>
                  </NavLink>
                ))}
              </div>
            </CollapsibleGroup>
          )}

          {isManager && (
            <CollapsibleGroup icon={ChartBar} label="Reports" open={reportsOpen} setOpen={setReportsOpen} testid="nav-reports-toggle">
              <div data-testid="nav-reports-group">
                {REPORTS.map(({ to, label }) => (
                  <NavLink key={to} to={to} end={to === "/reports"} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`} className={linkClass}>
                    <ChartBar size={16} weight="bold" /><span className="font-medium">{label}</span>
                  </NavLink>
                ))}
              </div>
            </CollapsibleGroup>
          )}
        </nav>
        <div className="px-3 pb-4">
          <a
            href="/Kapstone-OPS-User-Guide.pdf"
            download
            target="_blank"
            rel="noreferrer"
            data-testid="nav-user-guide"
            className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-[var(--text-2)] hover:bg-zinc-100 hover:text-[var(--text-1)] transition-colors duration-150 border border-[var(--border)]"
          >
            <BookOpen size={18} weight="bold" /><span className="font-medium">User Guide (PDF)</span>
          </a>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden min-w-0">
        {/* Unified top bar: hamburger (mobile) + right-side Settings & Account menus */}
        <div className="sticky top-0 z-30 flex items-center gap-3 px-4 md:px-8 py-3 bg-[var(--sidebar)] border-b border-[var(--border)]">
          <button data-testid="sidebar-open" onClick={() => setSidebarOpen(true)} className="md:hidden text-[var(--text-1)]"><List size={24} weight="bold" /></button>
          <div className="md:hidden heading font-black text-base">KAPSTONE OPS</div>

          <div className="ml-auto flex items-center gap-2">
            {isManager && <CommandPalette />}
            {isManager && (
              <button
                data-testid="alerts-menu-btn"
                onClick={() => nav("/reports/alerts")}
                title="Alerts"
                className="relative flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium text-[var(--text-2)] hover:bg-zinc-100 hover:text-[var(--text-1)] transition-colors"
              >
                <WarningCircle size={18} weight="bold" />
                <span className="hidden sm:inline">Alerts</span>
                {(overdueCount + pendingApprovals) > 0 && (
                  <span data-testid="alerts-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[var(--critical)] text-white text-[10px] font-bold flex items-center justify-center">{overdueCount + pendingApprovals}</span>
                )}
              </button>
            )}
            {isManager && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="settings-menu-btn" className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium text-[var(--text-2)] hover:bg-zinc-100 hover:text-[var(--text-1)] transition-colors">
                    <Gear size={18} weight="bold" /> <span className="hidden sm:inline">Settings</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[80vh] overflow-y-auto">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">Masters &amp; Settings</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {MASTERS.map(({ to, label, icon: Icon }) => (
                    <DropdownMenuItem key={to} data-testid={`menu-${label.toLowerCase().replace(/[\s&]+/g, "-").replace(/-+$/, "")}`} onClick={() => nav(to)}>
                      <Icon size={16} weight="bold" className="mr-2 text-[var(--text-3)]" /> {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu-btn" className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium text-[var(--text-2)] hover:bg-zinc-100 hover:text-[var(--text-1)] transition-colors">
                  <UserCircle size={20} weight="bold" />
                  <span className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="font-semibold text-[var(--text-1)]" data-testid="user-name">{user?.name}</span>
                    <span className="text-[10px] uppercase tracking-wider" data-testid="user-role">{roleLabels[role] || role}</span>
                  </span>
                  <CaretDown size={12} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-[var(--text-3)] font-normal">{roleLabels[role] || role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="change-password-btn" onClick={() => setShowChangePwd(true)}>
                  <Key size={16} weight="bold" className="mr-2 text-[var(--text-3)]" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="text-[var(--critical)] focus:text-[var(--critical)]">
                  <SignOut size={16} weight="bold" className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <Outlet />
      </main>
      <ChangePasswordModal open={showChangePwd} onClose={() => setShowChangePwd(false)} />
    </div>
  );
}
